'use client';
import React, {
  ComponentProps,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import Link from 'next/link';
import { animate, motion, useMotionValue } from 'framer-motion';
import { twMerge } from 'tailwind-merge';

// Utility function to lighten or darken a hex color
const lighten = (color: string, amount: number = 0.2): string => {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);

  let newR, newG, newB;

  if (amount >= 0) {
    // Lighten
    newR = Math.min(255, Math.floor(r + (255 - r) * amount));
    newG = Math.min(255, Math.floor(g + (255 - g) * amount));
    newB = Math.min(255, Math.floor(b + (255 - b) * amount));
  } else {
    // Darken
    const darkenAmount = Math.abs(amount);
    newR = Math.max(0, Math.floor(r * (1 - darkenAmount)));
    newG = Math.max(0, Math.floor(g * (1 - darkenAmount)));
    newB = Math.max(0, Math.floor(b * (1 - darkenAmount)));
  }

  return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
};

interface PurchaseButtonProps extends Omit<
  ComponentProps<'button'>,
  'onClick'
> {
  // Content props
  text?: string;
  children?: React.ReactNode;
  widthOverride?: string;

  // Behavior props
  href?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;

  // Element type
  as?: 'button' | 'link' | 'anchor';

  // Styling props
  className?: string;
  theme?: 'orange' | 'purple';
  flatHover?: boolean;
}

interface AnimatedGradientProps {
  uniqueId: string;
  theme: 'orange' | 'purple';
  disabled: boolean;
  isHovered: boolean;
  isPressed: boolean;
  flatHover: boolean;
}

const AnimatedGradient = ({
  disabled,
  flatHover,
  isHovered,
  isPressed,
  theme,
  uniqueId,
}: AnimatedGradientProps) => {
  const getGradientColors = () => {
    if (disabled) {
      return {
        stop1: '#E3E3E3',
        stop2: '#939393',
        x1: '0%',
        x2: '0%',
        y1: '0%',
        y2: '100%',
      };
    }

    if (theme === 'purple') {
      return {
        stop1: '#C3B5FF',
        stop2: '#8875D5',
        x1: '0%',
        x2: '0%',
        y1: '0%',
        y2: '100%',
      };
    }

    // Orange gradient (bottom to top)
    return {
      stop1: '#D64718',
      stop2: '#E98C6C',
      x1: '0%',
      x2: '0%',
      y1: '100%',
      y2: '0%',
    };
  };

  const colors = getGradientColors();
  const gradientId = `gradient-${uniqueId}`;

  // Use motion values for animated colors
  const stop1Color = useMotionValue(colors.stop1);
  const stop2Color = useMotionValue(colors.stop2);

  useEffect(() => {
    if (disabled) {
      stop1Color.set(colors.stop1);
      stop2Color.set(colors.stop2);
      return;
    }

    const { stop1, stop2 } = colors;

    let targetColor1, targetColor2;

    if (isPressed) {
      // Pressed state - solid color based on theme
      if (theme === 'purple') {
        targetColor1 = '#8875D5';
        targetColor2 = '#8875D5';
      } else {
        targetColor1 = '#D64718';
        targetColor2 = '#D64718';
      }
    } else if (isHovered && flatHover) {
      // For flat hover, use the same flat color for both stops
      // For purple theme, use the darker color (#8875D5)
      if (theme === 'purple') {
        targetColor1 = '#8875D5';
        targetColor2 = '#8875D5';
      } else {
        targetColor1 = stop1;
        targetColor2 = stop1;
      }
    } else if (isHovered) {
      // Regular gradient hover animation
      targetColor1 = lighten(stop1, -0.35);
      targetColor2 = lighten(stop2, -0.65);
    } else {
      targetColor1 = stop1;
      targetColor2 = stop2;
    }

    // Use slower transition for purple theme with flatHover
    const duration = theme === 'purple' && flatHover ? 1.2 : 0.6;

    const controls1 = animate(stop1Color, targetColor1, {
      duration: duration,
      ease: 'easeOut',
    });

    const controls2 = animate(stop2Color, targetColor2, {
      duration: duration,
      ease: 'easeOut',
    });

    return () => {
      controls1.stop();
      controls2.stop();
    };
  }, [
    isHovered,
    isPressed,
    theme,
    disabled,
    colors,
    stop1Color,
    stop2Color,
    flatHover,
  ]);

  return (
    <motion.svg
      width="100%"
      height="100%"
      className="pointer-events-none absolute top-0 left-0 z-10 rounded-[10px]"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
    >
      <defs>
        <motion.linearGradient
          id={gradientId}
          x1={colors.x1}
          y1={colors.y1}
          x2={colors.x2}
          y2={colors.y2}
          animate={{
            gradientTransform: isHovered
              ? [
                  'rotate(25) scale(1.4)',
                  'rotate(180) scale(0.8)',
                  'rotate(360) scale(1.8)',
                ]
              : 'rotate(0)',
          }}
          transition={{
            duration: 0.5,
            ease: 'easeOut',
          }}
        >
          <motion.stop
            offset={disabled ? '0%' : '0%'}
            style={{ stopColor: stop1Color }}
          />
          <motion.stop
            offset={disabled ? '68%' : theme === 'purple' ? '100%' : '68%'}
            style={{ stopColor: stop2Color }}
          />
        </motion.linearGradient>
      </defs>
      <rect width="100" height="100" fill={`url(#${gradientId})`} />
    </motion.svg>
  );
};

const AnimatedGradientButton = ({
  as = 'anchor',
  children,
  className = '',
  disabled = false,
  flatHover = true,
  form,
  href = '/hiv-test',
  onClick,
  text = 'Purchase A Kit',
  theme = 'orange',
  type = 'button',
}: PurchaseButtonProps) => {
  const uniqueId = useId();

  // Hover and pressed states for animation
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const touchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Touch event handlers to fix mobile hover state persistence
  const handleTouchStart = () => {
    setIsTouchDevice(true);
    setIsPressed(true);
    setIsHovered(false); // Immediately clear hover state on touch

    // Clear any existing timeout
    if (touchTimeoutRef.current) {
      clearTimeout(touchTimeoutRef.current);
    }
  };

  const handleTouchEnd = () => {
    setIsPressed(false);
    setIsHovered(false); // Ensure hover state is cleared

    // Clear any existing timeout
    if (touchTimeoutRef.current) {
      clearTimeout(touchTimeoutRef.current);
    }

    // Keep touch device flag active for a short time to prevent mouse events
    touchTimeoutRef.current = setTimeout(() => {
      setIsTouchDevice(false);
    }, 300);
  };

  // Modified mouse event handlers that respect touch device state
  const handleMouseEnter = () => {
    if (!isTouchDevice) {
      setIsHovered(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isTouchDevice) {
      setIsHovered(false);
      setIsPressed(false);
    }
  };

  // Base classes that are common to all instances (without margin-related classes)
  const baseClasses = `
    relative
    z-20
    text-white 
    rounded-[10px] 
    p-3 
    font-['Work_Sans'] 
    font-medium 
    text-base 
    flex 
    justify-center 
    items-center 
    tracking-[-2%] 
    text-center 
    align-middle
    z-10
    hover:cursor-pointer
  `
    .trim()
    .replace(/\s+/g, ' ');

  // Container classes for the outer element (includes margin classes from className)
  const containerClasses = `inline-block ${twMerge(className)}`.trim();

  const content = children || text;

  if (as === 'button') {
    return (
      <button
        type={type}
        className={containerClasses}
        onClick={onClick}
        disabled={disabled}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseDown={() => setIsPressed(true)}
        onMouseUp={() => setIsPressed(false)}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        form={form}
      >
        <div
          className={`relative overflow-hidden rounded-[10px] ${isPressed ? 'shadow-[0px_4px_12px_rgba(0,0,0,0.3)]' : ''}`}
        >
          <AnimatedGradient
            uniqueId={uniqueId}
            theme={theme}
            disabled={disabled}
            isHovered={isHovered}
            isPressed={isPressed}
            flatHover={flatHover}
          />
          <div className={baseClasses}>{content}</div>
        </div>
      </button>
    );
  }

  if (as === 'link') {
    return (
      <Link
        href={href}
        className={containerClasses}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseDown={() => setIsPressed(true)}
        onMouseUp={() => setIsPressed(false)}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div
          className={`relative overflow-hidden rounded-[10px] ${isPressed ? 'shadow-[0px_4px_12px_rgba(0,0,0,0.3)]' : ''}`}
        >
          <AnimatedGradient
            uniqueId={uniqueId}
            theme={theme}
            disabled={disabled}
            isHovered={isHovered}
            isPressed={isPressed}
            flatHover={flatHover}
          />
          <div className={baseClasses}>{content}</div>
        </div>
      </Link>
    );
  }

  // Check if href is internal (starts with / or #) or external
  const isInternalLink = href?.startsWith('/') || href?.startsWith('#');

  // For internal links, use Next.js Link; for external links, use anchor tag
  if (isInternalLink) {
    return (
      <Link
        href={href}
        className={containerClasses}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseDown={() => setIsPressed(true)}
        onMouseUp={() => setIsPressed(false)}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        prefetch={true}
      >
        <div
          className={`relative overflow-hidden rounded-[10px] ${isPressed ? 'shadow-[0px_4px_12px_rgba(0,0,0,0.3)]' : ''}`}
        >
          <AnimatedGradient
            uniqueId={uniqueId}
            theme={theme}
            disabled={disabled}
            isHovered={isHovered}
            isPressed={isPressed}
            flatHover={flatHover}
          />
          <div className={baseClasses}>{content}</div>
        </div>
      </Link>
    );
  }

  return (
    <a
      href={href}
      className={containerClasses}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseDown={() => setIsPressed(true)}
      onMouseUp={() => setIsPressed(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      target="_blank"
      rel="noopener noreferrer"
    >
      <div
        className={`relative overflow-hidden rounded-[10px] ${isPressed ? 'shadow-[0px_4px_12px_rgba(0,0,0,0.3)]' : ''}`}
      >
        <AnimatedGradient
          uniqueId={uniqueId}
          theme={theme}
          disabled={disabled}
          isHovered={isHovered}
          isPressed={isPressed}
          flatHover={flatHover}
        />
        <div className={baseClasses}>{content}</div>
      </div>
    </a>
  );
};

export default AnimatedGradientButton;
